import React from "react";
import { motion } from "framer-motion";
import {
  Disclosure,
  Transition,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { GoTrash } from "react-icons/go";
import { useTranslation } from "react-i18next";

import SessionHeader from "./SessionHeader";
import SessionDetails from "./SessionDetails";
import StatisticsSection from "./StatisticsSection";
import RoundHistorySection from "./RoundHistorySection";

/**
 * Individual session item component
 * @param {Object} props - Component props
 * @param {Object} props.session - Session data
 * @param {number} props.index - Session index for animation delay
 * @param {boolean} props.isExpanded - Whether the session is expanded
 * @param {Object} props.usedDart - Dart information
 * @param {boolean} props.proMember - Whether user is pro member
 * @param {Function} props.onToggle - Function to toggle session expansion
 * @param {Function} props.onDelete - Function to delete session
 */
const SessionItem = ({
  session,
  index,
  isExpanded,
  usedDart,
  proMember,
  onToggle,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <motion.li
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <Disclosure>
        {({ open }) => (
          <div
            className={`
              border border-primary/30 rounded-xl p-3 sm:p-4 
              transition-all duration-200
              bg-primary/80 
              hover:shadow-md hover:border-test/50
              ${isExpanded ? "ring-1 ring-test/30" : ""}
            `}
          >
            {/* Clickable Header */}
            <DisclosureButton
              as="div"
              onClick={onToggle}
              className="w-full cursor-pointer"
            >
              <SessionHeader
                session={session}
                usedDart={usedDart}
                isExpanded={isExpanded}
                onToggle={() => {}} // Empty function since onClick is handled by DisclosureButton
              />
            </DisclosureButton>

            {/* Expandable Content */}
            <Transition
              show={isExpanded}
              enter="transition-all ease-in-out duration-300"
              enterFrom="transform scale-95 opacity-0 max-h-0"
              enterTo="transform scale-100 opacity-100 max-h-[800px]"
              leave="transition-all ease-in-out duration-200"
              leaveFrom="transform scale-100 opacity-100 max-h-[800px]"
              leaveTo="transform scale-95 opacity-0 max-h-0"
            >
              <DisclosurePanel static>
                <div className="mt-3 sm:mt-4 text-white border-t border-primary/30 pt-3 sm:pt-4">
                  {/* Session Info */}
                  <SessionDetails session={session} />

                  {/* Statistics */}
                  <StatisticsSection session={session} proMember={proMember} />

                  {/* Round History */}
                  <RoundHistorySection session={session} />

                  {/* Delete Button */}
                  <button
                    onClick={() => onDelete(session.id)}
                    className="mt-3 sm:mt-4 bg-red-600/80 hover:bg-red-700 text-white py-2 px-3 rounded-lg transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
                  >
                    <GoTrash className="h-4 w-4" />
                    <span className="text-xs sm:text-sm">
                      {t("history.gameHistory.deleteSession")}
                    </span>
                  </button>
                </div>
              </DisclosurePanel>
            </Transition>
          </div>
        )}
      </Disclosure>
    </motion.li>
  );
};

export default SessionItem;
